import { DEFAULT_BRAND, type BrandDefinition } from "@school-sales-ai/shared";
import { cn } from "@/lib/utils";
import { LoxagoLogotype } from "./LoxagoLogotype";

/**
 * The active brand's wordmark — the single place the lockup's typography lives.
 *
 * The three morphemes run together into something unreadable ("…laops…") when
 * they share a weight and a colour, so they are split on both axes: the lead at
 * 700 in ink, the tail at 500 in the brand accent, the suffix at 400 in a muted
 * neutral. The TEXT comes from the brand registry; the weights and the neutrals
 * are the design system and do not vary.
 *
 * The accent flips with the GROUND, never with preference. For ScholaOps: on
 * light, brand `lime` is a 1.25:1 non-colour and `lime-deep` carries the text
 * (4.67:1 on white); on dark the roles invert and plain `lime` is the legible one
 * (14.5:1 on `ink-900`), where `lime-deep` would only reach 3.87:1. A new brand
 * owes the same two-value check before its pair goes in the registry.
 *
 * A brand whose wordmark is DRAWN rather than typeset (`kind: "logotype"`,
 * Loxago) renders its artwork instead, sized in `em` so every caller's font-size
 * class still sizes it. None of the rules above apply to it — they are about
 * splitting type, and a logotype has no type to split.
 *
 * Renders the wordmark only — callers own the mark and the gap,
 * because the sidebar, the login card and the privacy header each lock them up
 * at a different size.
 */
type WordmarkProps = {
  /**
   * Which brand to render. Defaults to the platform brand so a surface that has
   * no client yet (and no hostname to read) still renders something correct.
   *
   * The accent CLASS NAMES come off the definition rather than being assembled
   * here, and they are literals in the registry mirrored in `tailwind.config.ts`'s
   * safelist: Tailwind's JIT scans source text, so a class built from a registry
   * string is invisible to it and the colour silently disappears from the
   * production CSS. Inline hex is not the alternative — CLAUDE.md §6.10 forbids
   * inline `style`, and only `BrandMark` has an exemption (it renders inside the
   * widget's Shadow DOM, where no token layer exists).
   */
  brand?: BrandDefinition;
  /** The surface behind the wordmark. Selects the lime that clears AA on it. */
  ground?: "light" | "dark";
  /** Render the `.ai` suffix. Off where the lockup has to fit a narrow rail. Text wordmarks only. */
  showSuffix?: boolean;
  /** Size and spacing come from the caller — this sets colour and weight only. */
  className?: string;
};

export function Wordmark({
  brand = DEFAULT_BRAND,
  ground = "light",
  showSuffix = true,
  className,
}: WordmarkProps): React.JSX.Element {
  if (brand.wordmark.kind === "logotype") {
    return (
      <span className={cn("inline-flex items-center whitespace-nowrap", className)}>
        <LoxagoLogotype wordmark={brand.wordmark} ground={ground} label={brand.name} />
      </span>
    );
  }

  const dark = ground === "dark";
  const { lead, tail, suffix, accent } = brand.wordmark;
  return (
    <span
      className={cn("font-display tracking-tight whitespace-nowrap", className)}
      // The three spans are one word to a screen reader, and to anyone copying it.
      aria-label={showSuffix ? `${lead}${tail}${suffix}` : `${lead}${tail}`}
    >
      <span aria-hidden className={cn("font-bold", dark ? "text-white" : "text-ink-900")}>
        {lead}
      </span>
      <span aria-hidden className={cn("font-medium", dark ? accent.onDark : accent.onLight)}>
        {tail}
      </span>
      {showSuffix && (
        <span aria-hidden className={cn("font-normal", dark ? "text-ink-500" : "text-ink-300")}>
          {suffix}
        </span>
      )}
    </span>
  );
}
